import {
    Box,
    Button,
    Form,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IncomingThirdPartyName, IncomingThirdPartyStatus, IncomingThirdPartyType } from '@common/enums';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import type { IPractitioner } from '../../interfaces/practitioner';
import { UrlBuilder } from '../../../../common/utils/urlBuilder';
import { getJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { AdminProcessNullableFormattedDatetime, HeaderGroup, Loader } from '../../elements/common';
import { DisplayFullName } from '../../elements/DisplayFullName';
import { ListPager } from '../../elements/ListPager';

interface IRequest {
    processId: string;
}

interface IThirdPartyIncomingMessage {
    created_at: string | Date;
    httpStatusCode: number;
    id: string;
    practitioner: IPractitioner | null;
    request: IRequest | null;
    status: IncomingThirdPartyStatus;
    statusReason: string | null;
    thirdPartyName: IncomingThirdPartyName;
    thirdPartyType: IncomingThirdPartyType;
}

export function ThirdPartyIncomingMessages() {
    const [incomingMessages, setIncomingMessages] = useState<IThirdPartyIncomingMessage[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.thirdPartyIncomingMessages);
        search();
    }, []);

    return (
        <PageTemplate>
            <PageHeader title={translateKey('incomingMessages.incomingMessages')} />
            <Form
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    search();
                }}
                submitOnEnter={true}
            >
                <Box
                    alignItems="start"
                    display="grid"
                    gap={3}
                    gridTemplateColumns={['1fr', '380px max-content']}
                    mb={4}
                >
                    <TextInput
                        onChange={(e) => setSearchValue(e.target.value)}
                        value={searchValue}
                    />
                    <Button type="submit">{translateKey('common.search')}</Button>
                </Box>
            </Form>
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('incomingMessages.messageType')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.messageSender')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.status')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.statusMessage')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.receivedAt')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.practitioner')}</TableHeader>
                            <TableHeader>{translateKey('incomingMessages.pid')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading &&
                            incomingMessages.length > 0 &&
                            incomingMessages.map((incomingMessage) => (
                                <TableRow key={incomingMessage.id}>
                                    <TableCell>{incomingMessage.thirdPartyType}</TableCell>
                                    <TableCell>{incomingMessage.thirdPartyName}</TableCell>
                                    <TableCell>{incomingMessage.status}</TableCell>
                                    <TableCell>{incomingMessage.statusReason}</TableCell>
                                    <TableCell>
                                        <AdminProcessNullableFormattedDatetime value={incomingMessage.created_at} />
                                    </TableCell>
                                    <TableCell>
                                        <DisplayFullName {...incomingMessage.practitioner} />
                                    </TableCell>
                                    <TableCell>
                                        {incomingMessage.request ? incomingMessage.request.processId : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {loading && <Loader centered />}
            {!loading && incomingMessages.length === 0 && (
                <HeaderGroup
                    alignment={'center'}
                    header={translateKey('common.noResult')}
                />
            )}
            <ListPager
                isDisabled={loading}
                itemsPerPage={10}
                onChangePage={async (newPage) => {
                    setPage(newPage);
                    await search();
                }}
                page={page}
                total={total}
            />
        </PageTemplate>
    );

    async function search() {
        const urlBuilder = new UrlBuilder('/api/incomingThirdPartyMessages');
        if (searchValue) {
            urlBuilder.add('searchValue', searchValue);
        }
        urlBuilder.add('page', page).add('limit', 10);

        setLoading(true);
        const response = await getJson(urlBuilder.build());
        setLoading(false);

        setIncomingMessages(response.json.items);
        setTotal(response.json.total);
    }
}
